import { useFieldContext } from '@/lib/form.ts';
import NumberField, { NumberFieldProps } from '@/common/components/number-field/NumberField.tsx';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import { t } from '@lingui/core/macro';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: (issue) => {
    const minimum = issue.minimum as number;

    if (issue.inclusive) {
      return t`The number must be greater than or equal to ${minimum}`;
    } else {
      return t`The number must be greater than ${minimum}`;
    }
  },
};

export type FormNumberFieldProps = Omit<
  NumberFieldProps,
  'error' | 'value' | 'onValueChange' | 'onBlur' | 'helperText'
> & {
  errorMessage?: string;
};

export const FormNumberField = ({ errorMessage, ...props }: FormNumberFieldProps) => {
  const field = useFieldContext<number | null>();

  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  return (
    <NumberField
      {...props}
      fullWidth={props.fullWidth ?? true}
      value={field.state.value}
      onValueChange={field.handleChange}
      onBlur={handleBlur}
      error={shouldShowFieldErrors}
      helperText={
        shouldShowFieldErrors && (
          <>
            <FormFieldErrors zodIssueMap={zodIssueMap} />
            {errorMessage && (
              <span data-testid={`${field.name}-error`} className="block">
                {errorMessage}
              </span>
            )}
          </>
        )
      }
    />
  );
};
